import { useEffect, useRef, useCallback, useReducer } from 'react';
import { useSocket, useSocketEvent } from 'socket.io-react-hook';
import { _getToken, positionOpenerAxiosApi } from 'src/Config/axiosApi';
import {
  EnrichedEntryStatus,
  EntryOrdersResponse,
  OpeningPageState
} from '../types/entry.types';
import {
  LatencySample,
  recordReceived,
  useLatencyTracker
} from './useLatencyTracker';

export type { EnrichedEntryStatus } from '../types/entry.types';

export interface EntryExecutionEvent {
  ruleId: string;
  pairName: string;
  makerPlatform: string;
  makerPrice: number;
  counterPrice: number;
  contracts: number;
  tnaPercent: number;
  volumeUsd: number;
}

export interface EntryErrorEvent {
  ruleId: string;
  pairName: string;
  state: string;
  errorMessage: string;
}

type Action =
  | { type: 'STATUS'; payload: EnrichedEntryStatus[] }
  | { type: 'STATUS_UPDATE'; payload: EnrichedEntryStatus[] }
  | { type: 'EXECUTION'; payload: EntryExecutionEvent }
  | { type: 'ERROR'; payload: EntryErrorEvent }
  | { type: 'ORDERS'; payload: EntryOrdersResponse };

interface State {
  /** Map of pairId AND ruleId -> live entry state (mirrors unwinding hook) */
  states: Map<string, EnrichedEntryStatus>;
  lastExecution: EntryExecutionEvent | null;
  lastError: EntryErrorEvent | null;
  orders: EntryOrdersResponse | null;
}

const initialState: State = {
  states: new Map(),
  lastExecution: null,
  lastError: null,
  orders: null
};

/**
 * Strip the server's `_meta` envelope before passing into the reducer.
 * Mirrors `useUnwindingSocket`. Server wraps non-array payloads as
 * `{ ...payload, _meta }` and array payloads as `{ data: payload, _meta }`.
 */
/**
 * Phase 6 — project an `OpeningPageState` back into the legacy
 * `EnrichedEntryStatus` shape so existing row components keep working
 * during the bridging release. When `rule === null`, fills rule-scoped
 * fields with placeholders so the row still renders the market data.
 * Phase 7 removes this projection once the row components consume
 * `OpeningPageState` directly.
 */
function projectToLegacy(p: OpeningPageState): EnrichedEntryStatus {
  const m = p.market;
  const r = p.rule;
  const displayPath: 'a' | 'b' | null =
    r?.committedPath ??
    (r?.sumPathA != null && r?.sumPathB != null
      ? r.sumPathA <= r.sumPathB
        ? 'a'
        : 'b'
      : null);
  const polyBid =
    displayPath === 'b' ? m.polyNoBid : m.polyYesBid;
  const polyAsk =
    displayPath === 'b' ? m.polyNoAsk : m.polyYesAsk;
  const kalshiBid =
    displayPath === 'b' ? m.kalshiYesBid : m.kalshiNoBid;
  const kalshiAsk =
    displayPath === 'b' ? m.kalshiYesAsk : m.kalshiNoAsk;
  return {
    ruleId: r?.ruleId ?? p.pairId,
    pairId: p.pairId,
    state: r?.state ?? 'no_rule',
    makerPlatform: r?.makerPlatform ?? 'kalshi',
    makerOrderId: r?.makerOrderId ?? null,
    makerPrice: r?.makerPrice ?? null,
    makerFilledQty: r?.makerFilledQty ?? 0,
    makerFilledPrice: r?.makerFilledPrice ?? 0,
    counterOrderId: r?.counterOrderId ?? null,
    counterFilledQty: r?.counterFilledQty ?? 0,
    lastStateChange: r?.lastStateChange ?? new Date().toISOString(),
    errorMessage: r?.errorMessage ?? null,
    pairName: m.pairName,
    expiryDate: m.expiryDate,
    daysToExpiry: m.daysToExpiry,
    polyBid,
    polyAsk,
    kalshiBid,
    kalshiAsk,
    currentSumOfAsks:
      r?.committedPath === 'b'
        ? r?.sumPathB ?? null
        : r?.committedPath === 'a'
          ? r?.sumPathA ?? null
          : (r?.sumPathA ?? r?.sumPathB ?? null),
    simulation: r?.simulation ?? {
      polyAsMaker: null,
      kalshiAsMaker: null
    },
    kalshiConfig: r?.kalshiConfig ?? null,
    polymarketConfig: r?.polymarketConfig ?? null,
    maxVolumeUsd: r?.maxVolumeUsd ?? 0,
    volumePeriod: r?.volumePeriod ?? 'day',
    volumeUsed: r?.volumeUsed ?? 0,
    volumeRemaining: r?.volumeRemaining ?? 0,
    kalshiMarketUrl: m.kalshiMarketUrl,
    polymarketMarketUrl: m.polymarketMarketUrl,
    simTna: r?.simTna,
    simSize: r?.simSize,
    simMaxVolume: r?.simMaxVolume,
    appliedSimTna: r?.appliedSimTna,
    appliedSimSize: r?.appliedSimSize,
    appliedSimMaxVolume: r?.appliedSimMaxVolume,
    hasPendingSim: r?.hasPendingSim,
    polyYesBid: m.polyYesBid,
    polyYesAsk: m.polyYesAsk,
    polyNoBid: m.polyNoBid,
    polyNoAsk: m.polyNoAsk,
    kalshiYesBid: m.kalshiYesBid,
    kalshiYesAsk: m.kalshiYesAsk,
    kalshiNoBid: m.kalshiNoBid,
    kalshiNoAsk: m.kalshiNoAsk,
    // Sums + sim cells are computed at the envelope level (always,
    // rule or no rule) — read from `p.*` first so pre-rule rows
    // still see live values. Fall back to the rule slice for
    // backward-compat with older backend builds that emitted them
    // there.
    sumPathA: p.sumPathA ?? r?.sumPathA ?? null,
    sumPathB: p.sumPathB ?? r?.sumPathB ?? null,
    lowerPath: p.lowerPath ?? r?.lowerPath ?? null,
    committedPath: r?.committedPath ?? null,
    simPolyYesMaker: p.simPolyYesMaker ?? r?.simPolyYesMaker ?? null,
    simPolyNoMaker: p.simPolyNoMaker ?? r?.simPolyNoMaker ?? null,
    simKalshiYesMaker: p.simKalshiYesMaker ?? r?.simKalshiYesMaker ?? null,
    simKalshiNoMaker: p.simKalshiNoMaker ?? r?.simKalshiNoMaker ?? null,
    engineSim: p.engineSim ?? null,
    // Per-pair opener kill switch. Defaults false on manual-confirm, so
    // the row should render OFF when the field is unset. Forwarding it
    // verbatim here lets the row's switch read live state without
    // hitting REST.
    pairEnabled: p.pairEnabled === true
  };
}

function unwrap<T>(message: unknown): T {
  if (Array.isArray(message)) return message as T;
  if (
    message &&
    typeof message === 'object' &&
    '_meta' in (message as object)
  ) {
    const obj = message as Record<string, unknown> & { _meta?: unknown };
    if ('data' in obj) return obj.data as T;
    const rest = { ...obj };
    delete rest._meta;
    return rest as T;
  }
  return message as T;
}

export function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'STATUS': {
      const newMap = new Map<string, EnrichedEntryStatus>();
      for (const s of action.payload) {
        newMap.set(s.ruleId, s);
        if (s.pairId && s.pairId !== s.ruleId) newMap.set(s.pairId, s);
      }
      return { ...state, states: newMap };
    }
    case 'STATUS_UPDATE': {
      const merged = new Map(state.states);
      for (const s of action.payload) {
        merged.set(s.ruleId, s);
        if (s.pairId && s.pairId !== s.ruleId) merged.set(s.pairId, s);
      }
      return { ...state, states: merged };
    }
    case 'EXECUTION':
      return { ...state, lastExecution: action.payload };
    case 'ERROR':
      return { ...state, lastError: action.payload };
    case 'ORDERS':
      return { ...state, orders: action.payload };
    default:
      return state;
  }
}

export { initialState as entrySocketInitialState };

export function useEntrySocket() {
  const wsUrl = import.meta.env.VITE_POSITION_OPENER_URL;
  const accessToken = _getToken();

  const { socket } = useSocket((wsUrl || '') + '/opening', {
    enabled: !!wsUrl && !!accessToken,
    transports: ['websocket'],
    auth: (cb: (data: { token: string }) => void) =>
      cb({ token: _getToken() })
  });

  const [state, dispatch] = useReducer(reducer, initialState);
  const hasSubscribed = useRef(false);

  // REST seed: same pattern as `useUnwindingSocket` — fetch the latest
  // EnrichedEntryStatus[] snapshot when the WS isn't connected so the page
  // has data during the handshake or while the predictions subsystem is
  // OFF. Live WS deltas always override.
  useEffect(() => {
    if (!wsUrl || !accessToken) return;
    if (socket.connected) return;

    let cancelled = false;
    positionOpenerAxiosApi
      .get<EnrichedEntryStatus[]>('/states')
      .then(({ data }) => {
        if (cancelled) return;
        if (!Array.isArray(data)) return;
        dispatch({ type: 'STATUS_UPDATE', payload: data });
      })
      .catch(err => {
        if (cancelled) return;
        console.warn('[EntrySocket] REST seed failed:', err?.message ?? err);
      });

    return () => {
      cancelled = true;
    };
  }, [wsUrl, accessToken, socket.connected]);

  const latencyBuf = useRef<LatencySample[]>([]);
  useLatencyTracker(socket, latencyBuf);

  // Subscribe on every connect/reconnect.
  useEffect(() => {
    const subscribeIfNeeded = () => {
      if (!hasSubscribed.current) {
        socket.emit('subscribe_entry');
        hasSubscribed.current = true;
      }
    };
    const markDisconnected = () => {
      hasSubscribed.current = false;
    };

    socket.on('connect', subscribeIfNeeded);
    socket.on('disconnect', markDisconnected);

    if (socket.connected) subscribeIfNeeded();
    else markDisconnected();

    return () => {
      socket.off('connect', subscribeIfNeeded);
      socket.off('disconnect', markDisconnected);
    };
  }, [socket, socket.connected]);

  // This hook deliberately does NOT listen to the legacy `entry_status` /
  // `entry_status_update` events. The backend emits them alongside
  // `opening_state*` for old FE builds during a deploy, but they carry
  // the legacy `EnrichedRuleState` shape WITHOUT `engineSim`. Consuming
  // both streams into one Map (last-write-wins) made every
  // `entry_status_update` clobber the `engineSim` that the preceding
  // `opening_state_update` had set — the Sim columns flickered
  // value ↔ "—" on every tick. `opening_state*` is a superset (every
  // manual-confirmed pair, rule or not), so it is the single source of
  // truth here. Do not re-add the legacy listeners.
  const { lastMessage: executionMessage } =
    useSocketEvent<EntryExecutionEvent>(socket, 'entry_execution');

  const { lastMessage: errorMessage } = useSocketEvent<EntryErrorEvent>(
    socket,
    'entry_error'
  );

  useEffect(() => {
    if (executionMessage) {
      recordReceived(latencyBuf.current, 'entry_execution', executionMessage);
      dispatch({
        type: 'EXECUTION',
        payload: unwrap<EntryExecutionEvent>(executionMessage)
      });
    }
  }, [executionMessage]);

  useEffect(() => {
    if (errorMessage) {
      recordReceived(latencyBuf.current, 'entry_error', errorMessage);
      dispatch({
        type: 'ERROR',
        payload: unwrap<EntryErrorEvent>(errorMessage)
      });
    }
  }, [errorMessage]);

  // ── Phase 6 — split-shape listeners. Backend emits these alongside
  //    the legacy `entry_status` / `entry_status_update` for one
  //    bridging release. We project envelopes back to the legacy shape
  //    so the existing reducer + row components keep working. Phase 7
  //    deletes the legacy listeners + projection.
  const { lastMessage: openingStateMessage } = useSocketEvent<
    OpeningPageState[]
  >(socket, 'opening_state');
  const { lastMessage: openingStateUpdateMessage } = useSocketEvent<
    OpeningPageState[]
  >(socket, 'opening_state_update');

  useEffect(() => {
    if (openingStateMessage) {
      recordReceived(
        latencyBuf.current,
        'opening_state',
        openingStateMessage
      );
      const payload = unwrap<OpeningPageState[]>(openingStateMessage);
      if (!Array.isArray(payload)) return;
      dispatch({
        type: 'STATUS',
        payload: payload.map(projectToLegacy)
      });
    }
  }, [openingStateMessage]);

  useEffect(() => {
    if (openingStateUpdateMessage) {
      recordReceived(
        latencyBuf.current,
        'opening_state_update',
        openingStateUpdateMessage
      );
      const payload = unwrap<OpeningPageState[]>(openingStateUpdateMessage);
      if (!Array.isArray(payload)) return;
      dispatch({
        type: 'STATUS_UPDATE',
        payload: payload.map(projectToLegacy)
      });
    }
  }, [openingStateUpdateMessage]);

  const { lastMessage: ordersMessage } = useSocketEvent<EntryOrdersResponse>(
    socket,
    'entry_orders'
  );

  useEffect(() => {
    if (ordersMessage) {
      recordReceived(latencyBuf.current, 'entry_orders', ordersMessage);
      dispatch({
        type: 'ORDERS',
        payload: unwrap<EntryOrdersResponse>(ordersMessage)
      });
    }
  }, [ordersMessage]);

  const getState = useCallback(
    (idOrPair: string): EnrichedEntryStatus | undefined =>
      state.states.get(idOrPair),
    [state.states]
  );

  const injectStates = useCallback((states: EnrichedEntryStatus[]) => {
    if (!Array.isArray(states)) {
      console.warn(
        '[EntrySocket] injectStates called with non-array, ignoring:',
        states
      );
      return;
    }
    dispatch({ type: 'STATUS_UPDATE', payload: states });
  }, []);

  return {
    isConnected: socket.connected,
    states: state.states,
    getState,
    injectStates,
    lastExecution: state.lastExecution,
    lastError: state.lastError,
    orders: state.orders
  };
}
